/**
 * PdfPagePicker — a modal that lets staff CHOOSE which page of a multi-page PDF
 * is the floor plan, BEFORE it is rasterised + uploaded.
 *
 * ─────────────────────────────────────────────────────────────────────────
 *  WHY (owner, 2026-06-14): the owner's scans are often multi-page PDFs whose
 *  PAGE 1 is a report cover and the actual floor plan is on a LATER page. The
 *  upload flow used to auto-rasterise page 1 only, loading the wrong page. This
 *  picker surfaces every page as a small thumbnail (or a number selector with a
 *  live preview) so the staff pick the right one; the chosen page is then
 *  rasterised → uploaded → traced → reported exactly like a normal image upload.
 *
 *  A SINGLE-page PDF never reaches this picker (the parent rasterises page 1
 *  silently) — this modal only opens when numPages > 1.
 *
 *  PERFORMANCE: thumbnails render LAZILY + with bounded concurrency (a small
 *  pool), at a small scale (`renderPdfThumb`), so a 40-page PDF doesn't render
 *  all pages at once. The currently-selected page also renders a LARGER live
 *  preview so the staff can confirm the page even before its thumb finishes.
 *  All rendering reuses the bytes already in memory (no re-fetch).
 * ─────────────────────────────────────────────────────────────────────────
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Btn3 } from './ui';
import { renderPdfThumb } from '../lib/imageCropRotate';

/** How many thumbnails to render concurrently (a tiny pool keeps a big PDF from
 *  rendering every page at once + janking the UI). */
const THUMB_CONCURRENCY = 3;
/** Larger preview of the selected page (long-edge CSS px). */
const PREVIEW_MAX_PX = 560;

export default function PdfPagePicker({
  fileName,
  pageCount,
  pdfBytes,
  busy = false,
  onConfirm,
  onCancel,
}: {
  /** the PDF's file name (shown in the header). */
  fileName: string;
  /** total page count (the parent already detected it via getPdfPageCount). */
  pageCount: number;
  /** the PDF bytes (already read once) — reused to render thumbs + the preview
   *  WITHOUT re-reading the File. A fresh slice is taken per render because
   *  pdfjs `getDocument` consumes (transfers) the buffer it is given. */
  pdfBytes: ArrayBuffer;
  /** true while the parent rasterises + uploads the chosen page. */
  busy?: boolean;
  /** Confirm → rasterise + upload page `pageNum` (1-based). */
  onConfirm: (pageNum: number) => void;
  onCancel: () => void;
}) {
  const total = Math.max(1, Math.round(pageCount) || 1);
  // Default selection = page 1; the staff change it. (Page 1 is the common
  // "cover" case, but defaulting elsewhere would surprise single-plan PDFs.)
  const [selected, setSelected] = useState(1);
  // thumb data URLs by page number (1-based); '' = pending, 'ERR' = failed.
  const [thumbs, setThumbs] = useState<Record<number, string>>({});
  // larger preview of the SELECTED page.
  const [preview, setPreview] = useState<string>('');
  const [previewErr, setPreviewErr] = useState<string | null>(null);

  const pages = useMemo(
    () => Array.from({ length: total }, (_, i) => i + 1),
    [total],
  );

  // A fresh COPY of the bytes per render — pdfjs `getDocument({data})` transfers
  // (neuters) its buffer, so re-using the same ArrayBuffer twice throws. Slicing
  // gives each render its own copy while keeping the original intact for the next.
  const sliceBytes = useCallback(
    () => pdfBytes.slice(0),
    [pdfBytes],
  );

  // Esc cancels; lock body scroll while open.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !busy) onCancel();
    };
    window.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = prev;
    };
  }, [onCancel, busy]);

  // Lazily render thumbnails with bounded concurrency.
  useEffect(() => {
    let cancelled = false;
    const queue = pages.slice();
    let active = 0;

    const pump = () => {
      if (cancelled) return;
      while (active < THUMB_CONCURRENCY && queue.length) {
        const p = queue.shift() as number;
        active++;
        renderPdfThumb(sliceBytes(), p)
          .then((url) => {
            if (!cancelled) setThumbs((t) => ({ ...t, [p]: url }));
          })
          .catch(() => {
            if (!cancelled) setThumbs((t) => ({ ...t, [p]: 'ERR' }));
          })
          .finally(() => {
            active--;
            pump();
          });
      }
    };
    pump();
    return () => {
      cancelled = true;
    };
  }, [pages, sliceBytes]);

  // Render the larger preview whenever the selection changes.
  useEffect(() => {
    let cancelled = false;
    setPreview('');
    setPreviewErr(null);
    renderPdfThumb(sliceBytes(), selected, PREVIEW_MAX_PX)
      .then((url) => {
        if (!cancelled) setPreview(url);
      })
      .catch((e) => {
        if (!cancelled) setPreviewErr((e as Error).message);
      });
    return () => {
      cancelled = true;
    };
  }, [selected, sliceBytes]);

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Choose a PDF page"
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 1000,
        background: 'rgba(20,20,24,0.62)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 16,
      }}
      onPointerDown={(e) => {
        if (e.target === e.currentTarget && !busy) onCancel();
      }}
    >
      <div
        style={{
          background: '#fff',
          borderRadius: 'var(--r-md, 10px)',
          boxShadow: '0 18px 60px rgba(0,0,0,0.32)',
          width: 'min(960px, 96vw)',
          maxHeight: '94vh',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        {/* header */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 12,
            padding: '12px 16px',
            borderBottom: '1px solid var(--rule)',
          }}
        >
          <strong style={{ fontSize: 15 }}>Choose the floor-plan page</strong>
          <span
            style={{
              fontSize: 12,
              color: 'var(--ink-mute)',
              maxWidth: 360,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
            title={fileName}
          >
            {fileName} · {total} pages
          </span>
        </div>

        {/* page-number selector + live preview of the selected page */}
        <div
          style={{
            display: 'flex',
            gap: 8,
            flexWrap: 'wrap',
            alignItems: 'center',
            padding: '10px 16px',
            borderBottom: '1px solid var(--rule)',
          }}
        >
          <span style={{ fontSize: 12.5, color: 'var(--ink-mute)' }}>Page</span>
          <Btn3
            intent="ghost"
            size="sm"
            onClick={() => setSelected((p) => Math.max(1, p - 1))}
            disabled={busy || selected <= 1}
            aria-label="Previous page"
          >
            ‹
          </Btn3>
          <input
            type="number"
            value={selected}
            min={1}
            max={total}
            step={1}
            aria-label="Page number"
            onChange={(e) => {
              const n = Number(e.target.value);
              if (Number.isFinite(n)) {
                setSelected(Math.max(1, Math.min(total, Math.round(n))));
              }
            }}
            disabled={busy}
            style={{
              width: 72,
              padding: '5px 8px',
              border: '1px solid var(--rule-strong)',
              borderRadius: 'var(--r-sm)',
              fontSize: 13,
              fontFamily: 'inherit',
            }}
          />
          <span style={{ fontSize: 12.5, color: 'var(--ink-mute)' }}>
            of {total}
          </span>
          <Btn3
            intent="ghost"
            size="sm"
            onClick={() => setSelected((p) => Math.min(total, p + 1))}
            disabled={busy || selected >= total}
            aria-label="Next page"
          >
            ›
          </Btn3>
          <span style={{ flex: 1 }} />
          <span style={{ fontSize: 12, color: 'var(--ink-mute)' }}>
            Page {selected} of {total} selected
          </span>
        </div>

        {/* stage: large preview (left) + thumbnail grid (right) */}
        <div
          style={{
            flex: '1 1 auto',
            overflow: 'hidden',
            display: 'flex',
            minHeight: 280,
          }}
        >
          {/* live preview of the selected page */}
          <div
            style={{
              flex: '1 1 58%',
              background: '#f3f1ec',
              padding: 16,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              overflow: 'auto',
              borderRight: '1px solid var(--rule)',
            }}
          >
            {previewErr ? (
              <div
                style={{
                  fontSize: 12.5,
                  color: 'var(--crit, #dc2626)',
                  textAlign: 'center',
                  lineHeight: 1.6,
                  padding: 24,
                }}
              >
                Could not render that page.
                <div
                  style={{
                    fontSize: 11.5,
                    marginTop: 4,
                    color: 'var(--ink-mute)',
                  }}
                >
                  {previewErr}
                </div>
              </div>
            ) : preview ? (
              <img
                src={preview}
                alt={`Page ${selected}`}
                style={{
                  maxWidth: '100%',
                  maxHeight: '54vh',
                  objectFit: 'contain',
                  boxShadow: '0 2px 12px rgba(0,0,0,0.18)',
                  background: '#fff',
                }}
              />
            ) : (
              <span style={{ fontSize: 12.5, color: 'var(--ink-mute)' }}>
                Rendering page {selected}…
              </span>
            )}
          </div>

          {/* thumbnail grid (all pages, lazy) */}
          <div
            style={{
              flex: '1 1 42%',
              overflow: 'auto',
              padding: 12,
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(96px, 1fr))',
              gap: 10,
              alignContent: 'start',
            }}
          >
            {pages.map((p) => {
              const url = thumbs[p];
              const on = p === selected;
              return (
                <button
                  key={p}
                  type="button"
                  onClick={() => setSelected(p)}
                  disabled={busy}
                  title={`Page ${p}`}
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: 4,
                    padding: 4,
                    background: on ? 'var(--inset, #eef2f7)' : 'transparent',
                    border: on
                      ? '2px solid #2f5d8a'
                      : '1px solid var(--rule-strong)',
                    borderRadius: 'var(--r-sm)',
                    cursor: busy ? 'default' : 'pointer',
                    fontFamily: 'inherit',
                  }}
                >
                  <div
                    style={{
                      width: '100%',
                      aspectRatio: '3 / 4',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      background: '#fff',
                      border: '1px solid var(--rule)',
                      borderRadius: 4,
                      overflow: 'hidden',
                    }}
                  >
                    {url && url !== 'ERR' ? (
                      <img
                        src={url}
                        alt={`Page ${p}`}
                        style={{
                          maxWidth: '100%',
                          maxHeight: '100%',
                          objectFit: 'contain',
                        }}
                      />
                    ) : (
                      <span
                        style={{
                          fontSize: 11,
                          color:
                            url === 'ERR'
                              ? 'var(--crit, #dc2626)'
                              : 'var(--ink-mute)',
                        }}
                      >
                        {url === 'ERR' ? '⚠' : '…'}
                      </span>
                    )}
                  </div>
                  <span
                    style={{
                      fontSize: 11.5,
                      fontWeight: on ? 700 : 500,
                      color: on ? '#2f5d8a' : 'var(--ink)',
                    }}
                  >
                    {p}
                  </span>
                </button>
              );
            })}
          </div>
        </div>

        {/* footer */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 12,
            padding: '12px 16px',
            borderTop: '1px solid var(--rule)',
          }}
        >
          <span
            style={{ fontSize: 11.5, color: 'var(--ink-mute)', lineHeight: 1.4 }}
          >
            Pick the page that holds the floor plan. Only that page is uploaded;
            you can crop &amp; rotate it next.
          </span>
          <div style={{ display: 'flex', gap: 8 }}>
            <Btn3 intent="ghost" size="sm" onClick={onCancel} disabled={busy}>
              Cancel
            </Btn3>
            <Btn3
              intent="grn"
              size="sm"
              onClick={() => onConfirm(selected)}
              disabled={busy}
              title={`Upload page ${selected}`}
            >
              {busy ? 'Uploading…' : `Use page ${selected}`}
            </Btn3>
          </div>
        </div>
      </div>
    </div>
  );
}
